import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { type Path, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { type Location, useLocation, useNavigate } from "react-router-dom";

import {
  getActiveParticipantAccountPaymentAccountGet,
  getGetActiveParticipantAccountPaymentAccountGetQueryKey,
  getGetMyStudyParticipantsMeStudyGetQueryKey,
  getListMyLegalDocumentsParticipantsMeLegalDocumentsGetQueryKey,
  getListCardsPaymentAccountCardAllGetQueryKey,
  getMyStudyParticipantsMeStudyGet,
  LegalDocumentRequirementScope,
  listCardsPaymentAccountCardAllGet,
  type ParticipantLoginResponse,
  useAcceptLegalDocumentsLegalDocumentsAcceptPost,
  useListMyLegalDocumentsParticipantsMeLegalDocumentsGet,
  useLoginParticipantsAuthLoginPost,
  useOnboardParticipantsAuthOnboardPost,
} from "@/api-v3";
import { AppVersion } from "@/components/app-version";
import { ForgetPinButton } from "@/components/forget-pin-button";
import { FormErrorBox } from "@/components/form-error-box";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  APP_NAME,
  brandingAssetUrl,
  useTenantDisplayName,
} from "@/features/branding";
import { LegalDocumentsAgreementDialog } from "@/features/legal";
import {
  includesLanguage,
  LanguageButton,
  LanguageSelectorDialog,
} from "@/features/localisation";
import { ChangePINPage } from "@/features/settings";
import StorageManager from "@/features/storage";
import { useToast } from "@/hooks/use-toast";
import {
  getApiValidationErrors,
  isApiValidationError,
} from "@/lib/api-validation-errors";
import { parseError } from "@/lib/error-handler";
import { reset } from "@/store";
import { AppRoute } from "@/types/routes";

import {
  AuthForm,
  type AuthFormSchema,
  createLoginFormSchema,
  createRegisterFormSchema,
  getDefaultContractsFields,
} from "../../components";
import { buildTokenData } from "../../token-data";
import { type LoginData } from "../../types";

import { type AuthMode, type AuthPageLocationState } from "./auth-page.types";

/** Form fields the API can name in a 422, per mode. */
const AUTH_FIELDS: Record<AuthMode, readonly string[]> = {
  login: ["loginId", "pin"],
  register: ["loginId", "yearOfBirth", "pin"],
};

export const AuthPage = () => {
  const { t, i18n } = useTranslation();
  const toaster = useToast();
  const navigate = useNavigate();
  const location: Location<AuthPageLocationState> = useLocation();
  const queryClient = useQueryClient();
  const [authMode, setAuthMode] = useState<AuthMode>("login");
  const [error, setError] = useState<string>("");
  const [requiresPinChange, setRequiresPinChange] = useState(false);
  const [loadingPostAuthActions, setLoadingPostAuthActions] = useState(false);
  const [showLanguageSelector, setShowLangaugeSelector] = useState(false);
  const clientName = useTenantDisplayName() ?? APP_NAME;

  const defaultRedirectPath = AppRoute.HOME;
  const pathToIgnore = "/submit/";
  const { isInactive, from } = location.state || {};

  const isInactivityRedirect = Boolean(isInactive);
  let callbackURL: string = from?.pathname ?? defaultRedirectPath;

  if (callbackURL?.toString().includes(pathToIgnore)) {
    callbackURL = defaultRedirectPath;
  }

  const previousLoginDetails = StorageManager.getLoginInformation();
  const seededLogin = isInactivityRedirect ? previousLoginDetails : null;
  const hideLoginId = Boolean(seededLogin?.loginId);

  const { mutateAsync: sendLoginRequest, isPending: isLoginPending } =
    useLoginParticipantsAuthLoginPost({
      request: { skipGlobalErrorToast: true },
      mutation: {
        onError: (error) => {
          handleFailure(error);
        },
        onSuccess: async (response) => {
          if (!response) return;
          await handleAuthSuccess(response);
        },
      },
    });

  const { mutateAsync: sendOnboardRequest, isPending: isOnboardPending } =
    useOnboardParticipantsAuthOnboardPost({
      request: { skipGlobalErrorToast: true },
      mutation: {
        onError: (error) => {
          handleFailure(error as AxiosError);
        },
        onSuccess: async (response) => {
          await handleAuthSuccess(response);
        },
      },
    });

  const {
    data: pendingDocuments,
    isLoading: isContractsLoading,
    isError: getPendingDocumentsErrored,
    refetch: fetchPendingDocuments,
  } = useListMyLegalDocumentsParticipantsMeLegalDocumentsGet(
    {
      status: "pending",
    },
    { query: { enabled: authMode === "register" } }
  );

  const signUpContracts = useMemo(
    () =>
      (pendingDocuments ?? []).filter(
        (document) =>
          document.required_for === LegalDocumentRequirementScope.ALL_USERS
      ),
    [pendingDocuments]
  );

  const isLoadingContracts = authMode === "register" && isContractsLoading;
  const hasContractsErrored =
    authMode === "register" && getPendingDocumentsErrored;

  const schema = useMemo(
    () =>
      authMode === "login"
        ? createLoginFormSchema(t)
        : createRegisterFormSchema(t),
    [authMode, t]
  );

  const getFormDefaults = useCallback(() => {
    const baseDefaults = {
      loginId: seededLogin?.loginId ?? "",
      pin: seededLogin?.pin ?? "",
    };

    if (authMode === "register") {
      return {
        ...baseDefaults,
        yearOfBirth: "",
        confirmPin: "",
        contracts: getDefaultContractsFields(
          signUpContracts.map((contract) => contract.id)
        ),
      };
    }

    return baseDefaults;
  }, [seededLogin?.loginId, seededLogin?.pin, authMode, signUpContracts]);

  const form = useForm<AuthFormSchema>({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues: getFormDefaults(),
  });

  useEffect(() => {
    form.reset(getFormDefaults(), { keepValues: true });
  }, [form, getFormDefaults]);

  const amountOfPendingDocumentsCheck =
    pendingDocuments != null && pendingDocuments.length > 0;
  const openPendingDocumentsDialog =
    authMode === "login" && amountOfPendingDocumentsCheck;

  const { mutateAsync: acceptLegalDocuments, isPending: isAcceptingDocuments } =
    useAcceptLegalDocumentsLegalDocumentsAcceptPost({
      mutation: {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey:
              getListMyLegalDocumentsParticipantsMeLegalDocumentsGetQueryKey({
                status: "pending",
              }),
          });
        },
      },
    });

  const { refetch: getUserStudy } = useQuery({
    queryKey: getGetMyStudyParticipantsMeStudyGetQueryKey(),
    queryFn: getMyStudyParticipantsMeStudyGet,
    enabled: false,
  });

  const handleFailure = (error: AxiosError) => {
    if (isApiValidationError(error)) {
      // A hidden Login ID field cannot show or clear an error, so let it fall to the error box.
      const knownFields = AUTH_FIELDS[authMode].filter(
        (field) => field !== "loginId" || !hideLoginId
      );
      const { fieldErrors, formErrors } = getApiValidationErrors(error, {
        knownFields,
      });

      fieldErrors.forEach(({ field, message, type }, index) => {
        form.setError(
          field as Path<AuthFormSchema>,
          { type, message },
          { shouldFocus: index === 0 }
        );
      });

      setError(formErrors.map((formError) => formError.message).join("\n"));
      return;
    }

    const { message } = parseError(error);
    setError(t(message));
  };

  const handleAuthSuccess = async (loginResponse: ParticipantLoginResponse) => {
    const tokenData = buildTokenData(loginResponse);
    StorageManager.setTokenData(tokenData);
    if (tokenData.requiresPinChange) {
      setRequiresPinChange(true);
    } else {
      await checkIfUserLanguageIsSupported();
    }
  };

  useEffect(() => {
    if (isInactivityRedirect) {
      setAuthMode("login");
    }
  }, [isInactivityRedirect]);

  useEffect(() => {
    reset().catch((error: unknown) => {
      console.error(error);
    });
    if (!isInactivityRedirect) StorageManager.clear();
    // Runs on mount only. Re-running would purge the store mid-session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveAuthDataToStore = (authData: Record<string, unknown>) => {
    const authDataToSave = JSON.parse(JSON.stringify(authData));
    authDataToSave.userType = "participant";
    delete authDataToSave.pin;
    StorageManager.setLoginInformation(authDataToSave as LoginData);
  };

  const onSubmitForm = async (data: AuthFormSchema) => {
    setError("");

    try {
      if (authMode === "register" && "yearOfBirth" in data) {
        const registerData = {
          loginId: data.loginId,
          pin: data.pin,
          yearOfBirth: data.yearOfBirth,
          isNewUser: true,
        };
        saveAuthDataToStore(registerData);
        await sendOnboardRequest({
          data: {
            login_id: data.loginId,
            year_of_birth: Number.parseInt(data.yearOfBirth),
            pin: data.pin,
          },
        });
        await queryClient.invalidateQueries({
          queryKey:
            getListMyLegalDocumentsParticipantsMeLegalDocumentsGetQueryKey({
              status: "accepted",
            }),
        });
      } else {
        const loginData = {
          loginId: data.loginId,
          pin: data.pin,
          isNewUser: false,
        };
        saveAuthDataToStore(loginData);
        await sendLoginRequest({
          data: {
            login_id: loginData.loginId,
            pin: loginData.pin,
          },
        });
      }
    } catch {
      // The mutation's onError has already reported the failure.
    }
  };

  const checkIfUserLanguageIsSupported = async () => {
    try {
      setLoadingPostAuthActions(true);

      const { data: study, isError, error: studyError } = await getUserStudy();

      if (isError || study == null) {
        onPostAuthActionsError(studyError);
        return;
      }

      if (study.languages == null) {
        throw new Error(`Study ${study.study_name} has no languages set.`);
      }

      if (includesLanguage(study.languages, i18n.language)) {
        await checkUserLegalDocuments();
      } else {
        setShowLangaugeSelector(true);
      }
    } catch (err) {
      onPostAuthActionsError(err);
    }
  };

  const checkUserLegalDocuments = async () => {
    try {
      if (authMode === "login") {
        const pendingLegalDocuments = await fetchPendingDocuments();
        if (pendingLegalDocuments.isError) {
          onPostAuthActionsError(pendingLegalDocuments.error);
          return;
        }
        if (pendingLegalDocuments?.data?.length === 0) {
          await checkUserPayments();
        }
      } else {
        await checkUserPayments();
      }
    } catch (err) {
      onPostAuthActionsError(err);
    }
  };

  const hasUsableCard = async () => {
    try {
      const response = await queryClient.fetchQuery({
        queryKey: getListCardsPaymentAccountCardAllGetQueryKey(),
        queryFn: () => listCardsPaymentAccountCardAllGet(),
      });

      return (response?.data ?? []).length > 0;
    } catch {
      return false;
    }
  };

  const checkUserPayments = async () => {
    try {
      const payments = await queryClient.fetchQuery({
        queryKey: getGetActiveParticipantAccountPaymentAccountGetQueryKey(),
        queryFn: ({ signal }) =>
          getActiveParticipantAccountPaymentAccountGet(
            undefined,
            undefined,
            signal
          ),
      });

      const canUseApp = payments != null || (await hasUsableCard());

      setLoadingPostAuthActions(false);
      if (canUseApp) {
        navigate(callbackURL, { replace: true });
      } else {
        navigate(AppRoute.PAYMENT_METHODS_LIST, { replace: true });
      }
    } catch (err) {
      onPostAuthActionsError(err);
    }
  };

  const acceptPendingLegalDocuments = async () => {
    try {
      setLoadingPostAuthActions(true);
      await acceptLegalDocuments({
        data: {
          data: (pendingDocuments ?? []).map((document) => ({
            legal_document_id: document.id,
            version_id: document.version.id,
          })),
        },
      });
      await checkUserPayments();
    } catch (err) {
      onPostAuthActionsError(err);
    }
  };

  const onPostAuthActionsError = (error: unknown) => {
    setLoadingPostAuthActions(false);
    console.log("Failed to execute post login actions", error);
    toaster.toast({
      title: t("error"),
      description: t("unexpectedError"),
      variant: "destructive",
    });
  };

  const cancelReLogin = () => {
    StorageManager.removeLoginInformation();
    setAuthMode("register");
    navigate("/login");
  };

  const switchMode = () => {
    // When we switch mode ensure we are removing previously fetched data from when the user was not logged in
    queryClient.removeQueries({
      queryKey: getListMyLegalDocumentsParticipantsMeLegalDocumentsGetQueryKey({
        status: "pending",
      }),
    });
    setAuthMode(authMode === "login" ? "register" : "login");
    setError("");
    form.clearErrors();
  };

  const isSubmitting =
    isLoginPending || isOnboardPending || loadingPostAuthActions;

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Language Selector */}
        <div className="flex justify-end mb-2">
          <LanguageButton className="w-32 pr-0 border-none bg-transparent text-muted-foreground hover:bg-transparent" />
        </div>

        {/* Main Auth Container */}
        <div className="bg-card rounded-2xl shadow-lg p-8 animate-scale-in transition-all duration-500 ease-in-out">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              data-test-id="logo"
              src={brandingAssetUrl("logo")}
              alt={`${clientName} Logo`}
              className="h-logo-height object-contain"
            />
          </div>

          {/* Form Header */}
          <div className="text-center mb-8 animate-fade-in">
            <h1 className="text-2xl font-semibold text-foreground mb-2 transition-all duration-300">
              {!isInactivityRedirect
                ? authMode === "login"
                  ? t("signIn")
                  : t("auth_create_account")
                : t("session_expired")}
            </h1>
            <p className="text-muted-foreground">
              {t("payment_dynamic_description")}
            </p>
          </div>

          {/* Error Display */}
          {error !== "" && (
            <FormErrorBox dataTestId={`${authMode}-error`} error={error} />
          )}

          {/* Form */}

          <AuthForm
            form={form}
            mode={authMode}
            onSubmit={onSubmitForm}
            isSubmitting={isSubmitting}
            hideLoginId={hideLoginId}
            contracts={signUpContracts}
            isLoadingContracts={isLoadingContracts}
            hasContractsErrored={hasContractsErrored}
            refetchContracts={fetchPendingDocuments}
          />

          {/* Cancel button for inactivity redirect */}
          {isInactivityRedirect && (
            <Button
              onClick={cancelReLogin}
              variant="ghost"
              className="mt-4 w-full text-red-500 hover:text-red-500"
              type="button"
            >
              {t("cancel")}
            </Button>
          )}

          {/* Switch Mode */}
          {!isInactivityRedirect && (
            <div className="mt-12 text-center">
              <p className="text-muted-foreground mb-2">
                {authMode === "login"
                  ? t("auth_new_here")
                  : t("auth_already_signed_in_before")}
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 border-border hover:bg-accent transition-all duration-200 text-wrap"
                onClick={switchMode}
                data-testid={
                  authMode === "login"
                    ? "onboard-create-new-account-button"
                    : "back-to-sign-in-page-button"
                }
              >
                {authMode === "login"
                  ? t("auth_use_id_to_create")
                  : t("signIn")}
              </Button>
            </div>
          )}

          {/* Help Link */}
          <div className="mt-8 text-center">
            <ForgetPinButton
              {...(authMode === "register" && {
                title: t("auth_forgotPinAlertTitle"),
              })}
            />
          </div>
        </div>

        <AppVersion className="mt-4" />

        {/* Change PIN Dialog */}
        <Dialog open={requiresPinChange} onOpenChange={setRequiresPinChange}>
          <DialogContent className="max-w-md" dismissible={false}>
            <ChangePINPage
              isDialogMode={true}
              onSuccess={async () => {
                setRequiresPinChange(false);
                await checkIfUserLanguageIsSupported();
              }}
            />
          </DialogContent>
        </Dialog>

        <LanguageSelectorDialog
          open={showLanguageSelector}
          forcedSelection
          hideDialog={async () => {
            setShowLangaugeSelector(false);
            await checkUserLegalDocuments();
          }}
        />

        <LegalDocumentsAgreementDialog
          open={openPendingDocumentsDialog}
          contracts={pendingDocuments ?? []}
          isSubmitting={isAcceptingDocuments}
          isError={getPendingDocumentsErrored}
          onAccept={acceptPendingLegalDocuments}
          refetch={fetchPendingDocuments}
        />
      </div>
    </div>
  );
};
